import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  List,
  ListItem,
  Paper,
  Typography,
  CircularProgress,
  Avatar,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import scenarios from '../data/scenarios.json';
import { API_URL, getUiLanguage } from '../config';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import { useLanguage } from '../contexts/LanguageContext';

const ChatMessages = ({ 
  messages, 
  isLoading, 
  selectedScenario, 
  onRegenerateMessage,
  editingMessageId,
  setEditingMessageId,
  onEditMessage,
  conversationId,
  sendFeedback
}) => {
  const { tr, language, isEnglish } = useLanguage();
  const messagesEndRef = useRef(null);
  const [playingMessageId, setPlayingMessageId] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState({});
  const audioElementsRef = useRef({});
  const [showDebug] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('debug') === '1';
  });
  const [editedText, setEditedText] = useState('');
  const lastBotMessageIdRef = useRef(null); // Ref to track the last auto-played bot message
  const [dislikedMessages, setDislikedMessages] = useState({}); // State for disliked messages

  // Add debug logging before finding scenario
  useEffect(() => {
    console.log('Debug - Scenarios State:', {
      scenariosLength: scenarios?.length || 0,
      scenariosIsArray: Array.isArray(scenarios),
      selectedScenario,
      availableScenarios: scenarios.map(s => s.id)
    });
  }, [selectedScenario]);

  const currentScenario = scenarios.find(scenario => scenario.id === selectedScenario) || scenarios[0] || {};
  
  // Add debug logging
  useEffect(() => {
    console.log('Current Scenario:', currentScenario);
    console.log('Selected Scenario ID:', selectedScenario);
  }, [currentScenario, selectedScenario]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();

    // Auto-play logic
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      // Check if the last message is from the bot and hasn't been auto-played yet
      if (lastMessage.sender === 'bot' && lastMessage.id !== lastBotMessageIdRef.current) {
        // Check if audio is already playing, don't interrupt
        if (playingMessageId === null) {
           // Check if voice playback is enabled in localStorage
          const autoPlayEnabled = localStorage.getItem('autoPlayVoice') !== 'false'; // Default to true if not set

          if (autoPlayEnabled) {
            console.log(`Auto-playing message ID: ${lastMessage.id}`);
            handlePlay(lastMessage.id, lastMessage.text, lastMessage.scenario_id || selectedScenario);
            lastBotMessageIdRef.current = lastMessage.id; // Update the ref
          }
        }
      }
    }
  }, [messages]); // Depend only on messages

  useEffect(() => {
    return () => {
      Object.values(audioElementsRef.current).forEach(audio => {
        if (audio) {
          audio.pause();
        }
      });
    };
  }, []);

  const handlePlay = async (messageId, text, scenario_id) => {
    try {
      const currentAudio = audioElementsRef.current[messageId];

      // 如果正在播放當前音頻，則停止
      if (playingMessageId === messageId) {
        currentAudio?.pause();
        setPlayingMessageId(null);
        return;
      }

      // 停止其他正在播放的音頻
      if (playingMessageId && audioElementsRef.current[playingMessageId]) {
        audioElementsRef.current[playingMessageId].pause();
      }

      setLoadingAudio(prev => ({ ...prev, [messageId]: true }));

      // 如果這條消息還沒有音頻，則獲取
      if (!currentAudio) {
        const voiceSource = localStorage.getItem('voiceSource') || 'elevenlabs'; // Default to ElevenLabs
        
        // Get the voice IDs from the current scenario
        const cartesiaId = currentScenario?.cartesia_id || '';
        const elevenlabsId = currentScenario?.elevenlabs_id || '';
        
        const response = await axios.post(`${API_URL}/api/text-to-speech`, {
          text,
          scenario_id: scenario_id || selectedScenario,
          voice_source: isEnglish ? 'cartesia' : voiceSource,
          cartesia_id: cartesiaId,
          elevenlabs_id: elevenlabsId,
          language: getUiLanguage()
        });

        const audioMimeType = response.data.mime_type || 'audio/wav';
        const newAudio = new Audio(`data:${audioMimeType};base64,${response.data.audio}`);
        newAudio.onended = () => {
          setPlayingMessageId(null);
        };

        audioElementsRef.current[messageId] = newAudio;
        await newAudio.play();
      } else {
        // 使用現有的音頻
        currentAudio.currentTime = 0;
        await currentAudio.play();
      }

      setPlayingMessageId(messageId);

    } catch (error) {
      console.error('Error playing audio:', error);
    } finally {
      setLoadingAudio(prev => ({ ...prev, [messageId]: false }));
    }
  };

  return (
    <Box sx={{ 
      flex: 1, 
      overflowY: 'auto',
      p: 2
    }}>
      <List>
        {messages.map((message, index) => {
          const isEditing = message.id === editingMessageId;
          
          // Get all bot messages
          const botMessages = messages.filter(m => m.sender === 'bot');
          
          // Check if this is the last bot message in the conversation
          const isLastBotMessage = message.sender === 'bot' && 
                                  message.id === botMessages[botMessages.length - 1]?.id;
          
          // Check if this is the first bot message
          const isFirstBotMessage = message.sender === 'bot' && 
                                   message.id === botMessages[0]?.id;
          
          // Find the index of the current message within the botMessages array
          const botMessageIndex = message.sender === 'bot' ? botMessages.findIndex(bm => bm.id === message.id) : -1;
          
          // Only show regenerate button if it's the last bot message AND NOT the first bot message
          const showRegenerateButton = isLastBotMessage && !isFirstBotMessage;
          
          // Check if the current message is disliked
          const isDisliked = dislikedMessages[message.id];

          return (
            <ListItem
              key={message.id || Date.now()}
              sx={{
                justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                alignItems: 'flex-start',
                mb: 2,
                px: 2
              }}
            >
              {message.sender === 'bot' && (
                <Avatar
                  src={currentScenario.image || '/bot-avatar.png'}
                  sx={{ mr: 2, width: 40, height: 40, bgcolor: '#f0f0f0' }}
                />
              )}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  maxWidth: '70%',
                  backgroundColor: message.sender === 'user' ? '#e3f2fd' : '#f8f9fa',
                  borderRadius: 2,
                  position: 'relative'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, '& > p': { margin: 0, color: '#333' } }}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        variant="standard"
                        value={editedText}
                        onChange={(e) => setEditedText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            onEditMessage(message.id, editedText);
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            setEditingMessageId(null);
                          }
                        }}
                        multiline
                        maxRows={6}
                        InputProps={{
                          disableUnderline: true,
                          sx: {
                            px: 2,
                            py: 1,
                            '& textarea': {
                              color: '#333',
                              resize: 'none',
                              overflow: 'auto',
                              minHeight: '24px',
                              lineHeight: '1.5',
                            }
                          }
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            backgroundColor: 'background.paper',
                          }
                        }}
                      />
                    ) : (
                      <ReactMarkdown>{message.text}</ReactMarkdown>
                    )}
                  </Box>
                  
                  {message.sender === 'user' && !isEditing && (
                    <Tooltip title={tr('編輯訊息', 'Edit message')}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingMessageId(message.id);
                          setEditedText(message.text);
                        }}
                        sx={{
                          ml: 1,
                          color: 'text.secondary',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.04)'
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {isEditing && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={tr('儲存', 'Save')}>
                        <IconButton
                          size="small"
                          onClick={() => onEditMessage(message.id, editedText)}
                          sx={{
                            color: 'success.main',
                            '&:hover': { bgcolor: 'success.light' }
                          }}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={tr('取消', 'Cancel')}>
                        <IconButton
                          size="small"
                          onClick={() => setEditingMessageId(null)}
                          sx={{
                            color: 'error.main',
                            '&:hover': { bgcolor: 'error.light' }
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  
                  {message.sender === 'bot' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Tooltip title={playingMessageId === message.id ? tr('停止播放', 'Stop audio') : tr('播放語音', 'Play audio')}>
                        <IconButton
                          size="small"
                          onClick={() => handlePlay(message.id, message.text, message.scenario_id)}
                          disabled={loadingAudio[message.id]}
                          sx={{
                            ml: 1,
                            bgcolor: theme => playingMessageId === message.id ? theme.palette.primary.main : 'transparent',
                            color: theme => playingMessageId === message.id ? '#fff' : theme.palette.primary.main,
                            '&:hover': {
                              bgcolor: theme => playingMessageId === message.id ? theme.palette.primary.dark : 'rgba(0,0,0,0.04)'
                            }
                          }}
                        >
                          {loadingAudio[message.id] ? (
                            <CircularProgress size={20} />
                          ) : playingMessageId === message.id ? (
                            <PauseIcon fontSize="small" />
                          ) : (
                            <PlayArrowIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      {onRegenerateMessage && showRegenerateButton && (
                        <Tooltip title={tr('重新生成回答', 'Regenerate response')}>
                          <IconButton
                            size="small"
                            onClick={() => onRegenerateMessage(message)}
                            sx={{
                              ml: 1,
                              color: 'text.secondary',
                              '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.04)'
                              }
                            }}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
                <Typography 
                  variant="caption" 
                  color="textSecondary" 
                  sx={{ 
                    mt: 1, 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%'
                  }}
                >
                  <span>
                    {new Date(message.timestamp).toLocaleTimeString(language === 'en' ? 'en-US' : 'zh-TW', { timeZone: 'Asia/Taipei' })}
                    {message.isEdited && tr(' (已編輯)', ' (edited)')}
                    {showDebug && message.responseTime && ` (Response: ${message.responseTime}ms)`}
                    {showDebug && message.backendDuration && ` (Backend: ${message.backendDuration}ms)`}
                  </span>
                  {message.sender === 'bot' && botMessageIndex >= 1 && (
                    <Tooltip title={isDisliked ? tr('取消倒讚', 'Remove dislike') : tr('倒讚', 'Dislike')}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const isNowDisliked = !dislikedMessages[message.id];
                          setDislikedMessages(prev => ({ ...prev, [message.id]: isNowDisliked }));
                          console.log(`Message ${message.id} ${isNowDisliked ? 'disliked' : 'undisliked'}`);

                          // Call sendFeedback function from props if newly disliked
                          if (isNowDisliked && sendFeedback) {
                            sendFeedback('message', message.text, message.id);
                          }
                        }}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.04)'
                          }
                        }}
                      >
                        {isDisliked ? <ThumbDownAltIcon fontSize="small" /> : <ThumbDownAltOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Typography>
              </Paper>
            </ListItem>
          );
        })}
      </List>
      {isLoading && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={24} />
        </Box>
      )}
      <div ref={messagesEndRef} />
    </Box>
  );
};

export default ChatMessages;
